/* eslint-disable react/jsx-no-literals */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { isNative } from '../../platform/host';
import * as classic from '../../platform/classicPrinter';
import * as marklife from '../../platform/marklife';
import * as niimbot from '../../platform/niimbot';
import { listPrinterProfiles, previewBitmap, type LabelBitmap, type ManagedPrinterProfile } from './api';
import type { DirectPrintItem, PrinterMedium } from './ClassicPrinterPanel';
import {
  effectiveInversion,
  loadLastPrinterProfile,
  loadLocalPrinterDevice,
  rotateLabelBitmap,
  saveLastPrinterProfile,
  saveLocalPrinterDevice,
} from './managedPrinter';

interface Props {
  templateId: string;
  entityId?: string;
  copies?: number;
  items?: DirectPrintItem[];
  medium?: PrinterMedium;
  systemBusy: boolean;
  onSystemPrint: () => void;
  onDirectStart?: () => Promise<void>;
  onDirectPrinted?: () => Promise<void>;
  compact?: boolean;
}

function isConnected(profile: ManagedPrinterProfile): boolean {
  if (profile.driver === 'tspl_classic') return classic.current();
  if (profile.driver === 'niimbot_ble') return niimbot.current() !== null;
  if (profile.driver === 'marklife_p15_ble') return marklife.current() !== null;
  return true;
}

/** The ordinary print form deliberately knows no calibration values. */
export function ProfilePrinterPanel({
  templateId, entityId, copies = 1, items, medium, systemBusy, onSystemPrint, onDirectStart, onDirectPrinted, compact = false,
}: Props) {
  const profiles = useQuery({
    queryKey: ['labels', 'printer-profiles', templateId],
    queryFn: () => listPrinterProfiles({ enabled: true, status: 'ready', templateId }),
    enabled: Boolean(templateId),
  });
  const [profileId, setProfileId] = useState('');
  const [connected, setConnected] = useState(false);
  const [jobInverted, setJobInverted] = useState(false);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<classic.PairedPrinter[]>([]);
  const available = useMemo(() => profiles.data ?? [], [profiles.data]);
  const selected = useMemo(() => available.find((profile) => profile.id === profileId) ?? null, [available, profileId]);
  const printItems = items ?? (entityId ? [{ templateId, entityId, copies, medium }] : []);

  useEffect(() => {
    const remembered = loadLastPrinterProfile(templateId);
    const next = available.find((profile) => profile.id === remembered) ?? available[0] ?? null;
    setProfileId(next?.id ?? '');
    setConnected(next ? isConnected(next) : false);
    setJobInverted(false);
    setRotation(0);
    setError(null);
  }, [templateId, available]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await work(); } catch (exc) { setError(exc instanceof Error ? exc.message : 'print_failed'); }
    finally { setBusy(false); }
  };

  const connect = async () => {
    if (!selected) return;
    if (selected.driver === 'tspl_classic') {
      const address = loadLocalPrinterDevice(selected.id);
      if (isNative && !address) {
        const paired = await classic.paired();
        setDevices(paired);
        if (paired.length !== 1) throw new Error('choose_device');
        saveLocalPrinterDevice(selected.id, paired[0]?.address ?? '');
      }
      await classic.connect(loadLocalPrinterDevice(selected.id) || undefined);
    } else if (selected.driver === 'niimbot_ble') await niimbot.connect();
    else if (selected.driver === 'marklife_p15_ble') await marklife.connect();
    setConnected(true);
  };

  const print = async () => {
    if (!selected) return;
    if (selected.driver === 'system_pdf') {
      onSystemPrint();
      saveLastPrinterProfile(templateId, selected.id);
      return;
    }
    await onDirectStart?.();
    for (const item of printItems) {
      const itemMedium = item.medium ?? medium;
      if (selected.settings.driver === 'niimbot_ble' && (itemMedium?.heightMm ?? 0) > 12) {
        throw new Error('NIIMBOT D110 печатает этикетки шириной не более 12 мм. Шаблон 30×14 нельзя напечатать без искажения масштаба.');
      }
      const original: LabelBitmap = item.loadBitmap ? await item.loadBitmap() : await previewBitmap(item.templateId, item.entityId!);
      const bitmap = await rotateLabelBitmap(original, rotation);
      const inverted = effectiveInversion(selected.settings.polarity_correction, jobInverted);
      if (selected.settings.driver === 'tspl_classic') {
        await classic.print(bitmap.blob, 'tspl', item.copies, {
          inverted,
          density: selected.settings.density,
          speed: selected.settings.speed,
          feedMm: selected.settings.feed_mm,
          gapMm: selected.settings.gap_mm,
          gapOffsetMm: selected.settings.gap_offset_mm,
          mediaWidthMm: itemMedium?.widthMm,
          mediaHeightMm: itemMedium?.heightMm,
          headWidthMm: selected.settings.head_width_mm,
          mediaAlignment: selected.settings.media_alignment,
          xOffsetMm: selected.settings.x_offset_mm,
          yOffsetMm: selected.settings.y_offset_mm,
          scaleX: selected.settings.scale_x,
          scaleY: selected.settings.scale_y,
          qrAreas: bitmap.qrAreas,
        });
      } else if (selected.settings.driver === 'niimbot_ble') {
        await niimbot.print(bitmap.blob, { copies: item.copies, density: selected.settings.density, inverted, qrAreas: bitmap.qrAreas, mediaWidthMm: itemMedium?.widthMm });
      } else if (selected.settings.driver === 'marklife_p15_ble') {
        await marklife.print(bitmap.blob, { copies: item.copies, density: selected.settings.density, inverted, qrAreas: bitmap.qrAreas, mediaWidthMm: itemMedium?.widthMm });
      }
    }
    await onDirectPrinted?.();
    saveLastPrinterProfile(templateId, selected.id);
  };

  if (profiles.isLoading) return <p className="hint">Загрузка принтеров…</p>;
  if (!selected) return <p className="hint">Нет готового принтера для этого шаблона. <a href="/print/printers">Настроить принтеры</a></p>;

  return <section className={`printer-profile${compact ? ' printer-profile--compact' : ''}`}>
    <label>Принтер
      <select value={selected.id} onChange={(event) => { const next = available.find((item) => item.id === event.target.value); setProfileId(event.target.value); setConnected(next ? isConnected(next) : false); setError(null); }}>
        {available.map((profile) => <option key={profile.id} value={profile.id}>{profile.name} · {profile.model}</option>)}
      </select>
    </label>
    {selected.driver === 'system_pdf' ? <p className="hint">Системная печать: выберите этот профиль в диалоге Windows и масштаб 100 %.</p> : <>
      <p className="hint">{connected ? 'Подключён' : 'Не подключён'}</p>
      {isNative && selected.driver === 'tspl_classic' && !connected && devices.length > 1 && <label>Устройство
        <select value={loadLocalPrinterDevice(selected.id)} onChange={(event) => saveLocalPrinterDevice(selected.id, event.target.value)}>
          <option value="">Выберите сопряжённый принтер</option>{devices.map((device) => <option key={device.address} value={device.address}>{device.name || device.address}</option>)}
        </select>
      </label>}
      {!connected && <button type="button" disabled={busy} onClick={() => void run(connect)}>{busy ? 'Подключение…' : 'Подключиться'}</button>}
    </>}
    <details><summary>Дополнительные настройки задания</summary>
      <label className="check"><input type="checkbox" checked={jobInverted} onChange={(event) => setJobInverted(event.target.checked)} />Инверсия: белым по чёрному</label>
      <label>Поворот <select value={rotation} onChange={(event) => setRotation(Number(event.target.value) as 0 | 90 | 180 | 270)}><option value={0}>0°</option><option value={90}>90°</option><option value={180}>180°</option><option value={270}>270°</option></select></label>
    </details>
    <button type="button" className="primary" disabled={busy || systemBusy || (!connected && selected.driver !== 'system_pdf') || printItems.length === 0} onClick={() => void run(print)}>
      {busy ? 'Печать…' : selected.driver === 'system_pdf' ? 'Открыть системную печать' : 'Печатать напрямую'}
    </button>
    {error && <p className="error">{error}</p>}
  </section>;
}
